<template>
    <div class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20">
        <div class="rlms-flex rlms-items-center rlms-justify-between rlms-flex-wrap rlms-gap-10">
            <div class="rlms-fs-lg rlms-fw-600">Learners</div>
            <button class="rlms-btn rlms-btn-primary vue-add-learner-btn" @click="showAddLearnerModal = true" v-if="organization?.is_owner || organization?.is_administrator">Add New
                Learner</button>
        </div>

        <div id="vue-learner-list-container">
            <div class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20">
                <div class="rlms-table-filters">
                    <div class="scrollable-container">
                        <ul class="scrollable-content">
                            <li class="filter-tab" :class="{ 'active-filter': filter.type === 'all' }"><a
                                    href="javascript:void(0)" @click="fetchLearners('all')">All Learners</a></li>
                            <li class="filter-tab" :class="{ 'active-filter': filter.type === 'active' }"><a
                                    href="javascript:void(0)" @click="fetchLearners('active')">Active Learners</a></li>
                            <li class="filter-tab" :class="{ 'active-filter': filter.type === 'inactive' }"><a
                                    href="javascript:void(0)" @click="fetchLearners('inactive')">Inactive Learners</a>
                            </li>
                        </ul>
                    </div>
                    <div class="rlms-tf-actions">
                        <div class="input-icon-group input-icon-right">
                            <input type="search" placeholder="Search..." v-model="filter.search"
                                @input="handleSearch" />
                            <img class="rlms-input-icon" :src="searchIcon">
                        </div>
                    </div>
                </div>

                <!-- Data table -->
                <div class="rlms-table">
                    <div class="rlms-table-inner">
                        <table>
                            <thead>
                                <tr>
                                    <th>Learner</th>
                                    <th>
                                        <div class="rlms-flex rlms-items-center rlms-gap-2 rlms-cursor-pointer">Code
                                        </div>
                                    </th>
                                    <th>
                                        <div class="rlms-flex rlms-items-center rlms-gap-2 rlms-cursor-pointer">Created
                                            Date</div>
                                    </th>
                                    <th>
                                        <div class="rlms-flex rlms-items-center rlms-gap-2 rlms-cursor-pointer">Status
                                        </div>
                                    </th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-if="learners.length > 0">
                                    <tr v-for="(learner, i) in learners" :key="i">
                                        <td>
                                            <label
                                                class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-fc rlms-us-none">
                                                <div class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-mc"><img
                                                        :src="profileIcon">
                                                    <p class="rlms-fw-500">{{ learner.firstname }} {{ learner.lastname
                                                    }}</p>
                                                </div>
                                            </label>
                                        </td>
                                        <td>{{ learner.information_code }}</td>
                                        <td>
                                            <p class="rlms-text-extra-3">{{ formatDate(learner.created_at) }}</p>
                                        </td>
                                        <td>
                                            <div
                                                :class="`rlms-bg-${learner.setup ? 'success' : 'danger'}-100 rlms-text-${learner.setup ? 'success' : 'danger'}-600 rlms-py-1 rlms-px-4 rlms-w-fc rlms-br-16 rlms-fw-500`">
                                                {{ learner.setup ? 'Active' : 'Inactive' }}
                                            </div>
                                        </td>
                                        <td>
                                            <RouterLink :to="`/admin/organizations/${organizationId}/learners/${learner.id}`"
                                                class="rlms-flex rlms-items-center rlms-gap-5 rlms-cursor-pointer">
                                                <img :src="eyeIcon">
                                                <p class="rlms-text-primary-500">View</p>
                                            </RouterLink>
                                        </td>
                                    </tr>
                                </template>
                                <tr v-else>
                                    <td colspan="5">
                                        <div class="rlms-flex rlms-flex-col rlms-gap-30 rlms-text-center rlms-p-3">
                                            <img :src="noDataImage" class="rlms-mx-auto"
                                                style="width: clamp(150px, -1533rem + 3200vw, 150px);">
                                            <p class="rlms-text-center">No record found.</p>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    </div>
                </div>
            </div>
        </div>
        <paginate v-model="page" :page-count="totalPages" :margin-pages="2" :prev-text="'<'" :next-text="'>'"
            :container-class="'pagination'" :page-class="'page-item'" :active-class="'active-page'"
            v-if="totalPages > 1" />
    </div>
</template>
<script setup>
import { useRoute, RouterLink } from 'vue-router'
import { onMounted, ref, watch } from 'vue'
import axios from "@/services/axios";
import { formatDate } from '@/utils/format-helpers';
import { debounce } from '@/utils/debounce';
import paginate from 'vuejs-paginate-next';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';
import searchIcon from '@/assets/images/search.svg'
import profileIcon from '@/assets/images/profile.svg'
import eyeIcon from '@/assets/images/eye.svg'
import noDataImage from '@/assets/images/no-data-img.png'

const route = useRoute();
const loader = useLoaderStore();

const page = ref(1);
const totalPages = ref(1);
const totalItems = ref(0);
const organizationId = Number(route.params.organization ?? 0);
const organization = ref(null);
const learners = ref([]);
const selectedLearners = ref([]);
const showAddLearnerModal = ref(false);

const filter = ref({
    type: 'all',
    search: ''
});

onMounted(async () => {
    await init();
});

const init = async () => {
    selectedLearners.value = [];
    filter.value.type = 'all';
    filter.value.search = '';
    page.value = 1;

    showAddLearnerModal.value = false;
    await fetchLearners();
}

const fetchLearners = async (type = filter.value.type, search = filter.value.search) => {
    try {
        loader.show();

        const { data } = await axios.get(`/admin/organizations/${organizationId}/learners`, {
            params: {
                type,
                search,
                page: page.value,
                per_page: 10
            }
        });

        organization.value = data.data.organization;
        learners.value = data.data.learners.data;

        // Optional: store pagination meta if needed
        totalPages.value = data.data.learners.last_page;
        totalItems.value = data.data.learners.total;

        filter.value.type = type;
        filter.value.search = search;
    } catch (error) {
        handleError(error)
    } finally {
        loader.hide();
    }
};


watch(page, () => {
    fetchLearners();
});

const handleSearch = debounce(function () {
    page.value = 1;
    fetchLearners(filter.value.type, filter.value.search);
}, 400);
</script>